// Pure parsing/decision helpers for HTTP 429 ("tooManyRequests") handling. Zero-dep
// on purpose (no zustand, no react) so this module can be imported from the
// axios interceptor, the query provider, AND react-query's `retry` option without ever
// dragging UI/store code into the axios client's module graph.

export const TOO_MANY_REQUESTS_CODE = 'tooManyRequests';
export const DEFAULT_WAIT_MS = 60_000;
export const MAX_WAIT_MS = 60 * 60 * 1000;

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// Matches "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" ANYWHERE in the input —
// not anchored — so callers can pass the raw `message` field ("Reset Time =>
// 2026-07-30 12:00:24") straight through without stripping the prefix themselves.
const KST_DATETIME_RE = /(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/;

/**
 * Parses a KST wall-clock string (no timezone offset in the source) into epoch ms,
 * treating it as +09:00.
 *
 * NEVER use `new Date(string)` here: the space-separated form ("2026-07-30
 * 12:00:24") is outside the ECMAScript Date Time String Format grammar, so the
 * runtime either returns Invalid Date (NaN) or silently falls back to parsing it as
 * the DEVICE's local time — which produces a wrong instant on any device outside
 * KST. We instead extract the numeric fields with a regex and build the instant by
 * hand via `Date.UTC(...) - 9h`, which is timezone-independent regardless of the
 * host device's locale/TZ.
 */
export function parseKstDateTime(s: string): number | null {
  const match = KST_DATETIME_RE.exec(s);
  if (!match) return null;
  const [, y, mo, d, h, mi, se] = match;
  const utcMs = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se));
  return utcMs - KST_OFFSET_MS;
}

/** Inverse of `parseKstDateTime`: epoch ms → "YYYY-MM-DD HH:mm:ss" KST string
 *  (second precision). Derived from the UTC getters of `epochMs + 9h`, NOT from
 *  local getters — the device's own timezone must never leak in. Exported for the
 *  follow-up story / QA harness to render/verify countdown deadlines. */
export function formatKstDateTime(epochMs: number): string {
  const kst = new Date(epochMs + KST_OFFSET_MS);
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = kst.getUTCFullYear();
  const mo = pad(kst.getUTCMonth() + 1);
  const d = pad(kst.getUTCDate());
  const h = pad(kst.getUTCHours());
  const mi = pad(kst.getUTCMinutes());
  const se = pad(kst.getUTCSeconds());
  return `${y}-${mo}-${d} ${h}:${mi}:${se}`;
}

function extractStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const e = error as { response?: { status?: unknown }; status?: unknown };
  const status = e.response?.status ?? e.status;
  return typeof status === 'number' ? status : undefined;
}

/** True for ANY 429, regardless of body `code` — used by react-query's `retry` to
 *  suppress retries on rate limiting (retrying a 429 only amplifies the limit). */
export function isRateLimited(error: unknown): boolean {
  return extractStatus(error) === 429;
}

/** True only for the specific 429 + `code: "tooManyRequests"` shape the backend
 *  uses to signal the global rate-limit overlay (narrower than `isRateLimited`). */
export function isTooManyRequestsBody(status: number | undefined, body: unknown): boolean {
  if (status !== 429) return false;
  if (typeof body !== 'object' || body === null) return false;
  return (body as { code?: unknown }).code === TOO_MANY_REQUESTS_CODE;
}

function isValidCandidate(c: number, now: number): boolean {
  return Number.isFinite(c) && c > now && c <= now + MAX_WAIT_MS;
}

/**
 * Resolves the absolute instant (epoch ms) the caller may retry at, via a ladder:
 * `body.debug.ttl` → `parseKstDateTime(body.message)` → `now + DEFAULT_WAIT_MS`.
 *
 * A candidate is only accepted when `Number.isFinite(c) && c > now && c <= now +
 * MAX_WAIT_MS` — otherwise the ladder falls through to the next rung. That window
 * is the only guard against a backend timezone change or a skewed device clock
 * turning a normal rate-limit into an hours-long (or already-past) countdown, so a
 * candidate is NEVER rescaled (e.g. multiplying a seconds-looking ttl by 1000) to
 * force it into range — `message` is the independent cross-check rung, and a
 * silent rescale would defeat the sanity window entirely.
 */
export function resolveRetryAt(body: unknown, now: number = Date.now()): number {
  const b =
    typeof body === 'object' && body !== null
      ? (body as { debug?: { ttl?: unknown }; message?: unknown })
      : {};

  const ttl = typeof b.debug?.ttl === 'number' ? b.debug.ttl : Number.NaN;
  if (isValidCandidate(ttl, now)) return ttl;

  const message = typeof b.message === 'string' ? b.message : '';
  const parsed = parseKstDateTime(message);
  if (parsed !== null && isValidCandidate(parsed, now)) return parsed;

  return now + DEFAULT_WAIT_MS;
}
